import React, { useEffect, useState } from "react";
import { api, API as API_BASE, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Send, Download, History, Mail, CheckCircle2, AlertCircle, Loader2, Award } from "lucide-react";

/**
 * Admin → "Governance Send" panel
 *
 * Lets the trustees email the full review bundle (Cover Letter +
 * Governance Pack + Platform Fact Sheet) to a named reviewer
 * (caseworker, solicitor, mentor charity, prospective trustee, funder).
 * The trustees can also download the bundle as a zip and email it manually.
 *
 * Every send is logged to db.governance_sends and shown in the history
 * table at the bottom of the panel.
 */
export default function GovernanceSendManager() {
  const [form, setForm] = useState({
    to_email: "",
    recipient_name: "",
    recipient_org: "",
    subject: "",
    review_purpose: "",
    custom_message: "",
    include_cover_letter: true,
    include_fact_sheet: true,
  });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const r = await api.get("/admin/governance/sends");
      setHistory(r.data.items || []);
    } catch (_e) {
      // ignore
    }
    setLoadingHistory(false);
  };

  useEffect(() => { loadHistory(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.to_email) {
      toast.error("Recipient email is required.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/governance/send-pack", {
        to_email: form.to_email,
        recipient_name: form.recipient_name || null,
        recipient_org: form.recipient_org || null,
        subject: form.subject || null,
        review_purpose: form.review_purpose || null,
        custom_message: form.custom_message || null,
        include_cover_letter: form.include_cover_letter,
        include_fact_sheet: form.include_fact_sheet,
      });
      if (data.mocked) {
        toast.info("Email queued in MOCK mode (no RESEND_API_KEY set).");
      } else if (data.delivered) {
        toast.success(`Sent to ${data.to_email} with ${data.attachments.length} attachment(s).`);
      } else {
        toast.error("Resend rejected the message. Check backend logs.");
      }
      setForm({ ...form, to_email: "", recipient_name: "", recipient_org: "", custom_message: "" });
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  // build a query string for the bundle download so the cover letter gets
  // personalised even when downloading manually.
  const dlQuery = new URLSearchParams();
  if (form.recipient_name) dlQuery.set("recipient_name", form.recipient_name);
  if (form.recipient_org) dlQuery.set("recipient_org", form.recipient_org);
  if (form.review_purpose) dlQuery.set("review_purpose", form.review_purpose);
  if (form.custom_message) dlQuery.set("custom_message", form.custom_message);
  const qs = dlQuery.toString() ? `?${dlQuery.toString()}` : "";

  return (
    <div className="space-y-10" data-testid="governance-send-manager">
      <CharityStatusPanel />

      {/* CIO submission checklist link */}
      <div className="bg-saffron/10 border border-saffron/40 rounded-sm p-5 flex items-start gap-4" data-testid="cio-checklist-link-card">
        <Award size={20} className="text-saffron shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-display text-lg text-navy">CIO submission checklist — 15-step interactive playbook</h3>
          <p className="text-sm text-slate-700 mt-1">
            Open the in-app checklist when you sit down to fill in the gov.uk
            application. Progress is saved in your browser.
          </p>
        </div>
        <a
          href="/admin/cio-checklist"
          className="px-4 py-2 bg-navy text-pearl text-xs rounded-sm hover:bg-navy-light shrink-0 self-center"
          data-testid="open-cio-checklist"
        >
          Open checklist →
        </a>
      </div>

      {/* Send form */}
      <section className="bg-white border border-navy/10 rounded-sm p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <span className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-saffron/15 text-saffron border border-saffron/40">
            <Mail size={18} />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-2xl text-navy">Send governance pack for review</h2>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed max-w-2xl">
              Email the full review bundle &mdash; Cover Letter &middot; Governance Pack
              &middot; Platform Fact Sheet &mdash; to a caseworker, solicitor, mentor
              charity, prospective trustee or funder. The reviewer receives a branded
              HTML email with PDFs attached and direct links to every governance page.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Recipient email *" required>
            <input
              type="email"
              required
              value={form.to_email}
              onChange={(e) => setForm({ ...form, to_email: e.target.value })}
              placeholder="caseworker@charitycommission.gov.uk"
              data-testid="gov-send-email"
              className="w-full border border-navy/15 px-4 py-2.5 rounded-sm focus:outline-none focus:border-gold text-sm"
            />
          </Field>
          <Field label="Recipient name">
            <input
              value={form.recipient_name}
              onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
              placeholder="e.g. Ms Sample Caseworker"
              data-testid="gov-send-name"
              className="w-full border border-navy/15 px-4 py-2.5 rounded-sm focus:outline-none focus:border-gold text-sm"
            />
          </Field>
          <Field label="Recipient organisation">
            <input
              value={form.recipient_org}
              onChange={(e) => setForm({ ...form, recipient_org: e.target.value })}
              placeholder="e.g. Charity Commission for England and Wales"
              data-testid="gov-send-org"
              className="w-full border border-navy/15 px-4 py-2.5 rounded-sm focus:outline-none focus:border-gold text-sm"
            />
          </Field>
          <Field label="Subject line (optional)">
            <input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Defaults to ABGN-UK — Governance pack for review"
              data-testid="gov-send-subject"
              className="w-full border border-navy/15 px-4 py-2.5 rounded-sm focus:outline-none focus:border-gold text-sm"
            />
          </Field>
          <Field label="Review purpose (one short phrase)" cols={2}>
            <input
              value={form.review_purpose}
              onChange={(e) => setForm({ ...form, review_purpose: e.target.value })}
              placeholder="e.g. review of our forthcoming CIO application"
              data-testid="gov-send-purpose"
              className="w-full border border-navy/15 px-4 py-2.5 rounded-sm focus:outline-none focus:border-gold text-sm"
            />
          </Field>
          <Field label="Cover note (replaces default email body if filled)" cols={2}>
            <textarea
              value={form.custom_message}
              onChange={(e) => setForm({ ...form, custom_message: e.target.value })}
              placeholder="Optional. Leave blank for the standard cover note."
              rows={4}
              data-testid="gov-send-message"
              className="w-full border border-navy/15 px-4 py-2.5 rounded-sm focus:outline-none focus:border-gold text-sm"
            />
          </Field>

          <div className="md:col-span-2 flex flex-wrap items-center gap-5 pt-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.include_cover_letter}
                onChange={(e) => setForm({ ...form, include_cover_letter: e.target.checked })}
                data-testid="gov-send-include-cover"
              />
              Include Cover Letter PDF
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.include_fact_sheet}
                onChange={(e) => setForm({ ...form, include_fact_sheet: e.target.checked })}
                data-testid="gov-send-include-fact"
              />
              Include Platform Fact Sheet PDF
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-500 italic">
              Governance Pack is always attached.
            </label>
          </div>

          <div className="md:col-span-2 mt-2 flex flex-wrap gap-3 items-center">
            <button
              type="submit"
              disabled={sending}
              data-testid="gov-send-submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-navy text-pearl rounded-sm text-sm font-medium hover:bg-navy-light disabled:opacity-60"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              {sending ? "Sending…" : "Send review bundle"}
            </button>
            <a
              href={`${API_BASE}/governance/bundle.zip${qs}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gov-send-download-zip"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-navy/20 text-navy rounded-sm text-sm hover:bg-navy/5"
            >
              <Download size={15} /> Download zip instead
            </a>
            <span className="text-xs text-slate-500">
              The zip uses the same personalisation you typed above (recipient + purpose).
            </span>
          </div>
        </form>

        <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <DlLink href={`${API_BASE}/governance/cover-letter.pdf${qs}`} label="Preview Cover Letter PDF" testid="gov-preview-cover" />
          <DlLink href={`${API_BASE}/governance/pack.pdf`} label="Preview Governance Pack PDF" testid="gov-preview-pack" />
          <DlLink href={`${API_BASE}/governance/platform-fact-sheet.pdf`} label="Preview Platform Fact Sheet PDF" testid="gov-preview-fact" />
        </div>
      </section>

      {/* History */}
      <section className="bg-white border border-navy/10 rounded-sm p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
          <History size={18} className="text-saffron" />
          <h3 className="font-display text-xl text-navy">Sent history</h3>
          <button
            type="button"
            onClick={loadHistory}
            data-testid="gov-send-history-refresh"
            className="ml-auto text-xs text-slate-500 hover:text-navy"
          >
            Refresh
          </button>
        </div>
        {loadingHistory ? (
          <p className="text-sm text-slate-500">Loading history…</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No governance bundles have been sent yet.</p>
        ) : (
          <div className="overflow-x-auto" data-testid="gov-send-history-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-navy/10 text-xs uppercase tracking-widest text-slate-500">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">To</th>
                  <th className="py-2 pr-3">Recipient</th>
                  <th className="py-2 pr-3">Subject</th>
                  <th className="py-2 pr-3">Attachments</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.send_id} className="border-b border-navy/5 align-top">
                    <td className="py-2.5 pr-3 text-slate-600 whitespace-nowrap">
                      {new Date(h.sent_at).toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-3 text-navy">{h.to_email}</td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      {h.recipient_name || "—"}
                      {h.recipient_org ? <span className="block text-xs text-slate-400">{h.recipient_org}</span> : null}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">{h.subject}</td>
                    <td className="py-2.5 pr-3 text-xs text-slate-500">
                      {(h.attachments || []).join(" + ")}
                    </td>
                    <td className="py-2.5 pr-3">
                      {h.mocked ? (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                          <AlertCircle size={12} /> Mock
                        </span>
                      ) : h.delivered ? (
                        <span className="inline-flex items-center gap-1 text-green-700 text-xs">
                          <CheckCircle2 size={12} /> Delivered
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-700 text-xs">
                          <AlertCircle size={12} /> Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, children, required, cols }) {
  return (
    <label className={`block ${cols === 2 ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-[0.18em] text-slate-500">
        {label}{required ? " *" : ""}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function DlLink({ href, label, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="inline-flex items-center justify-between gap-2 px-3 py-2 border border-navy/10 rounded-sm text-navy hover:bg-navy/5"
    >
      <span>{label}</span>
      <Download size={12} />
    </a>
  );
}

// ─── Charity Status (post-approval scaffolding) ───────────────────────────
// Trustees use this once the Charity Commission grants registration: paste
// the charity number, set status to "registered", tick the gift-aid box —
// the entire site updates instantly via the public /api/charity/status feed.
function CharityStatusPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/admin/charity/status");
      setS(r.data);
      setDraft(r.data);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/charity/status", {
        status: draft.status || "in_preparation",
        charity_number: draft.charity_number || null,
        registered_at: draft.registered_at || null,
        case_reference: draft.case_reference || null,
        gift_aid_enabled: !!draft.gift_aid_enabled,
        bank_account_opened: !!draft.bank_account_opened,
        bookkeeping_started: !!draft.bookkeeping_started,
        notes: draft.notes || null,
      });
      setS(r.data);
      setDraft(r.data);
      toast.success("Charity status saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
    setSaving(false);
  };

  if (!s) {
    return (
      <section className="bg-white border border-navy/10 rounded-sm p-6">
        <p className="text-sm text-slate-500">Loading charity status…</p>
      </section>
    );
  }

  const isRegistered = s.status === "registered" && !!s.charity_number;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <section
      className="bg-white border border-navy/10 rounded-sm p-6 sm:p-8"
      data-testid="charity-status-panel"
    >
      <div className="flex items-start gap-4">
        <span className={`inline-flex items-center justify-center w-11 h-11 rounded-sm border ${
          isRegistered ? "bg-green-50 border-green-300 text-green-700" : "bg-saffron/15 border-saffron/40 text-saffron"
        }`}>
          <Award size={18} />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-2xl text-navy">Charity status &amp; post-approval scaffolding</h2>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed max-w-2xl">
            While the CIO application is in preparation, this stays as
            "Charity registration in progress". The moment the Charity
            Commission grants registration, paste the charity number, set
            status to <em>Registered</em>, and the whole site — footer,
            donate page, governance pack — updates instantly via the public
            <code className="bg-pearl px-1 rounded ml-1 text-[11px]">/api/charity/status</code> feed.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Application status">
          <select
            value={draft.status || "in_preparation"}
            onChange={(e) => set("status", e.target.value)}
            data-testid="charity-status-select"
            className="w-full border border-navy/15 px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-gold"
          >
            <option value="in_preparation">Application in preparation</option>
            <option value="submitted">Submitted to Commission</option>
            <option value="under_review">Under review (caseworker assigned)</option>
            <option value="registered">Registered ✅</option>
          </select>
        </Field>
        <Field label="Charity Commission case reference">
          <input
            value={draft.case_reference || ""}
            onChange={(e) => set("case_reference", e.target.value)}
            placeholder="e.g. 12345-678 (assigned after submission)"
            data-testid="charity-case-ref"
            className="w-full border border-navy/15 px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-gold"
          />
        </Field>
        <Field label="Charity number (paste once approved)">
          <input
            value={draft.charity_number || ""}
            onChange={(e) => set("charity_number", e.target.value)}
            placeholder="e.g. 1234567"
            data-testid="charity-number"
            className="w-full border border-navy/15 px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-gold"
          />
        </Field>
        <Field label="Registration date">
          <input
            type="date"
            value={(draft.registered_at || "").slice(0, 10)}
            onChange={(e) => set("registered_at", e.target.value || null)}
            data-testid="charity-registered-at"
            className="w-full border border-navy/15 px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-gold"
          />
        </Field>
        <Field label="Internal notes" cols={2}>
          <textarea
            value={draft.notes || ""}
            onChange={(e) => set("notes", e.target.value)}
            rows={2}
            placeholder="Caseworker name, latest queries, deadlines…"
            data-testid="charity-notes"
            className="w-full border border-navy/15 px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-gold"
          />
        </Field>
      </div>

      <div className="mt-6">
        <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">Post-approval milestones</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Toggle
            label="Gift Aid enabled"
            help="Show the Gift Aid declaration on the donate flow"
            checked={!!draft.gift_aid_enabled}
            onChange={(v) => set("gift_aid_enabled", v)}
            testid="charity-gift-aid"
            disabled={draft.status !== "registered"}
          />
          <Toggle
            label="Charity bank account opened"
            help="Tick once the CIO has its own bank account"
            checked={!!draft.bank_account_opened}
            onChange={(v) => set("bank_account_opened", v)}
            testid="charity-bank"
          />
          <Toggle
            label="Bookkeeping started"
            help="Tick once accounting software / records are in place"
            checked={!!draft.bookkeeping_started}
            onChange={(v) => set("bookkeeping_started", v)}
            testid="charity-bookkeeping"
          />
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="charity-status-save"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-navy text-pearl rounded-sm text-sm font-medium hover:bg-navy-light disabled:opacity-60"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
          {saving ? "Saving…" : "Save status"}
        </button>
        {s.updated_at && (
          <span className="text-xs text-slate-500">
            Last updated: {new Date(s.updated_at).toLocaleString()}
          </span>
        )}
      </div>
    </section>
  );
}

function Toggle({ label, help, checked, onChange, testid, disabled }) {
  return (
    <label className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer ${
      disabled ? "bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed" :
      checked ? "bg-green-50 border-green-300" : "bg-white border-navy/10 hover:border-navy/30"
    }`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="mt-0.5"
      />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-navy">{label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{help}</div>
        {disabled && <div className="text-[10px] text-saffron mt-1 uppercase tracking-wider">Set status to Registered first</div>}
      </div>
    </label>
  );
}
